import { createContext, type ReactNode, useContext, useEffect, useMemo, useRef } from "react"

import { useAuthStore } from "@/features/auth/store"
import { createHubConnection, type HubConnectionLike, type HubName } from "@/lib/api/signalr"

/**
 * SignalR provider. ADR #011: mock/real branch signalr.ts içinde.
 * Bileşenler sadece `HubConnectionLike` interface'ine bağlanır.
 * `register(hubName)` lazy connection döner; aynı hub için aynı instance'ı
 * paylaşır; unmount'ta kapatır.
 */

interface SignalRApi {
  register: (hubName: HubName) => HubConnectionLike
}

const SignalRContext = createContext<SignalRApi | null>(null)

export function SignalRProvider({ children }: { children: ReactNode }) {
  const connections = useRef<Map<HubName, HubConnectionLike>>(new Map())

  const api = useMemo<SignalRApi>(
    () => ({
      register: (hubName) => {
        const cached = connections.current.get(hubName)
        if (cached) {
          return cached
        }
        const conn = createHubConnection(hubName, () => useAuthStore.getState().accessToken)
        connections.current.set(hubName, conn)
        return conn
      },
    }),
    [],
  )

  useEffect(() => {
    const map = connections.current
    return () => {
      map.forEach((c) => {
        void c.stop()
      })
      map.clear()
    }
  }, [])

  return <SignalRContext.Provider value={api}>{children}</SignalRContext.Provider>
}

export function useSignalR(): SignalRApi {
  const ctx = useContext(SignalRContext)
  if (!ctx) {
    throw new Error("useSignalR must be used within <SignalRProvider>")
  }
  return ctx
}
